import {
  Alert,
  Box,
  Button,
  Paper,
  TextField
} from "@mui/material";
import moment from "moment";
import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { EntryField, EntryTitle, EntryWrapper, FormWrapper, } from "../../../components/customStyles/form";
import LocalStatusDialog from "../../../components/modal/LocalStatusDialog";
import Progress from "../../../components/progress/Progress";
import DataTable from "../../../components/table/data/DataTable";
import { useLocalScansColumns, useUserNotificationsColumns } from "../../../hooks/useTableColumns";
import IAdminPageBuildingDetail from "../../../models/adminPage/adminPageBuildingDetail";
import ICleaningSchedule, { defaultCleaningSchedule } from "../../../models/cleaningSchedule";
import IFloor from "../../../models/floor";
import IProfile from "../../../models/profile";
import ILocalEditRequest, { defaultLocalRequest } from "../../../models/requests/local/localEditRequest";
import { defaultClosingSchedule, defaultOpeningSchedule } from "../../../models/Schedule";
import { IScanDisplayWithId } from "../../../models/stat";
import IUserNotificationDetail from "../../../models/userNotificationDetail";
import eslService from "../../../services/esl/EslService";
import CleaningScheduleService from "../../../services/local/CleaningScheduleService";
import localService from "../../../services/local/LocalService";
import profileService from "../../../services/profile/ProfileService";
import hbscanStatService from "../../../services/stat/HBScanStatService";
import userNotificationService from "../../../services/userNotification/UserNotificationService";
import macAddressUtils from "../../../utils/macAddressUtils";
import LocalFormFields, { isServicePlusStickerCode } from "./LocalFormFields";
import { routes } from "../../../routes";
import EditOnly from "../../../components/permissions/EditOnly";

const ROWS_PER_PAGE = 10;
const SUCCESS_STATUS_CODE = 200;
const MIN_DATE = new Date(0);
const MAX_DATE = new Date(Date.now())


interface LocalData {
  id: number;
  name: string;
  floorId: number;
  propertyGroupId: number;
  tagMacAddress?: string;
  codes: string[];
  profileId: number;
  isLocked: boolean;
  alarmCode?: string;
  specification?: string;
  buildingId: number;
  cleaningSchedule?: ICleaningSchedule;
  buildings?: IAdminPageBuildingDetail[];
  localTenantIds?: number[];
  servicePlusStickerCode?: string;
}
const LocalEditPage = () => {
  const { t } = useTranslation(["common", "admin"]);

  const scansColumns = useLocalScansColumns();
  const userNotificationsColumns = useUserNotificationsColumns({ hasLocalName: false, hasUserName: true });

  const navigate = useNavigate();
  const location = useLocation();
  const { localData } = location.state;
  const [errorMessage, setErrorMessage] = useState("");

  const [localState, setLocalState] = useState<ILocalEditRequest>(defaultLocalRequest);
  const [cachedLocal, setCachedLocal] = useState<ILocalEditRequest>(defaultLocalRequest);
  const [isLocalChanged, setIsLocalChanged] = useState(false);

  const [buildings, setBuildings] = useState<IAdminPageBuildingDetail[]>([]);
  const [buildingFloors, setBuildingFloors] = useState<IFloor[]>([]);
  const [buildingProfiles, setBuildingProfiles] = useState<IProfile[]>([]);

  const [localScans, setLocalScans] = useState<IScanDisplayWithId[]>([]);
  const [userNotifications, setUserNotifications] = useState<IUserNotificationDetail[]>([]);

  const [isFormSending, setIsFormSending] = useState(false);
  const [isScansLoading, setIsScansLoading] = useState(true);
  const [isUserNotificationsLoading, setIsUserNotificationsLoading] = useState(true);

  const [isStatusConfirmationOpen, setIsStatusConfirmationOpen] = useState(false);


  const convertToLocalEditRequest = (localData: LocalData): ILocalEditRequest => {
    return {
      id: localData.id,
      name: localData.name,
      floorId: localData.floorId,
      tagMacAddress: localData.tagMacAddress || "",
      codes: localData.codes,
      profileId: localData.profileId,
      isLocked: localData.isLocked,
      buildingId: localData.buildingId,
      propertyGroupId: localData.propertyGroupId || 0,
      localCleaning: localData.cleaningSchedule || defaultCleaningSchedule,
      specification: localData.specification,
      localTenantIds: localData.localTenantIds || [],
      servicePlusStickerCode: localData.servicePlusStickerCode
    };
  };

  const onUpdateLocal = async (event: FormEvent) => {
    event.preventDefault();

    setErrorMessage("");
    setIsFormSending(true);

    if (localState.tagMacAddress) {
      const isEslMacValid = await eslService.GetIsValidMac(localState.tagMacAddress || "");

      if (!isEslMacValid) {
        setErrorMessage("table.local.macAddressErrorMessage");
        setIsFormSending(false);
        return;
      }
    }

    localService.PutLocal({ ...localState, openingSchedule: defaultOpeningSchedule, closingSchedule: defaultClosingSchedule })
      .then(async (response) => {
        if (response.status === SUCCESS_STATUS_CODE) {
          await CleaningScheduleService.PutCleaningSchedule(localState.localCleaning);
          navigate(routes.tableRoute.locals);
          return;
        }
        setErrorMessage("table.local.editErrorMessage");
      })
      .finally(() => setIsFormSending(false));
  };

  const getAlarmCode = (localId: number) => {
    localService.GetAlarmCode(localId).then((alarmCode) => {
      setLocalState((local) => ({
        ...local,
        alarmCode
      }));
      setCachedLocal((local) => ({
        ...local,
        alarmCode
      }));
    })
  }

  useEffect(() => {
    if (localState.buildingId === 0) return;

    profileService.GetFloorsAndProfileByBuilding(localState.buildingId).then((profileFloor) => {
      setBuildingFloors(profileFloor.floors)
      setBuildingProfiles(profileFloor.profiles)
    });

    if (localState.buildingId === cachedLocal.buildingId) return setLocalState(cachedLocal);

    setLocalState((prevState) => ({
      ...prevState,
      floorId: 0,
      profileId: 0,
    }));
  }, [localState.buildingId]);


  const fetchLocalData = (data: LocalData) => {
    setBuildings(data.buildings || []);

    const localEditRequest = convertToLocalEditRequest(data);
    setLocalState(localEditRequest);
    setCachedLocal(localEditRequest);

    const propertyGroupId = data.buildings?.find((b: IAdminPageBuildingDetail) => b.id === data.buildingId)?.propertyGroupId || 0;

    hbscanStatService.getPropertyGroupScans(propertyGroupId, MIN_DATE, MAX_DATE).then((scans) => {
      if (!scans) return;

      const filteredScans = scans
        .filter(scan => scan.start.localId === data.id)
        .sort((a, b) => moment(b.start.date).diff(moment(a.start.date)));

      const scansWithId: IScanDisplayWithId[] = filteredScans.map((scan, index) => ({
        ...scan.start,
        id: index,
        minutes: scan.minutes.toString(),
        endDate: scan.end.date,
      }));

      setLocalScans(scansWithId);
      setIsScansLoading(false);
    });

    userNotificationService.GetUserNotificationsByLocalId(data.id).then((userNotifs) => {
      if (!userNotifs) return;

      const sortedNotifications = userNotifs.sort((a, b) => moment(b.date).unix() - moment(a.date).unix());
      setUserNotifications(sortedNotifications);
      setIsUserNotificationsLoading(false);
    });
  };


  useEffect(() => {
    const data = localData || (location.state as LocalData);

    if (data) {
      fetchLocalData(data);
    }
  }, [localData]);



  useEffect(() => {
    setIsLocalChanged(false);
    if (JSON.stringify(cachedLocal) !== JSON.stringify(localState)) {
      setIsLocalChanged(true);
    }
    if (JSON.stringify(localState.localCleaning) !== JSON.stringify(cachedLocal.localCleaning)) {
      setIsLocalChanged(true);
    }
  }, [localState, cachedLocal]);

  const onToggleLocalStatus = () => {
    localService.PutLocalStatus(localState.id, !localState.isLocked)
      .then((response) => {
        if (response) {
          navigate(routes.tableRoute.locals);
          return;
        }
        setErrorMessage("table.local.editErrorMessage");
      })
  };

  return (
    <>
      <Box component={Paper} sx={{ width: "100%", position: "relative" }}>
        <form autoComplete="off" onSubmit={onUpdateLocal}>
          <FormWrapper >
            {errorMessage && (
              <Alert
                severity="error"
                sx={{ marginBottom: "1em" }}
              >
                {t(errorMessage)}
              </Alert>
            )}

            <LocalFormFields
              localState={localState}
              setLocalState={setLocalState}
              buildings={buildings}
              buildingFloors={buildingFloors}
              buildingProfiles={buildingProfiles}
              localCleaning={localState.localCleaning}
              getAlarmCode={() => {
                if (localState.alarmCode || localState.alarmCode === "") return;
                getAlarmCode(localState.id)
              }}
            />

            <EntryWrapper>
              <EntryTitle>{t("table.local.status")}</EntryTitle>
              <EntryField>
                <TextField
                  disabled
                  style={{
                    textAlign: "left",
                    width: "100%",
                  }}
                  value={localState.isLocked ? t("table.local.inactive") : t("table.local.active")}
                  onChange={(e) =>
                    setLocalState((prevState) => ({
                      ...prevState,
                      isLocked: e.target.value === t("table.local.inactive")
                    }))
                  }
                />
              </EntryField>
            </EntryWrapper>

            <LocalStatusDialog
              isOpen={isStatusConfirmationOpen}
              onClose={() => setIsStatusConfirmationOpen(false)}
              onToggleLocalStatus={onToggleLocalStatus}
              isLocalLocked={localState.isLocked || false}
            />

            <EntryWrapper
              sx={{
                justifyContent: "flex-end",
                marginBlock: "1em",
                gap: "1em",
              }}
            >
              <EditOnly>
                <Button
                  variant="contained"
                  color={localState.isLocked ? "success" : "error"}
                  onClick={() => setIsStatusConfirmationOpen(true)}
                  sx={{
                    marginLeft: "1em",
                  }}
                >
                  {localState.isLocked ? t("table.local.activateLocal") : t("table.local.deactivateLocal")}
                </Button>
              </EditOnly>

              <Progress isLoading={isFormSending}>
                <EditOnly>
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    disabled={
                      !localState.name.trim() || !localState.buildingId || !localState.floorId ||
                      !localState.profileId || localState.codes.length === 0 || !isLocalChanged || !macAddressUtils.isMacAdrress(localState.tagMacAddress ?? "")
                      || !isServicePlusStickerCode(localState.servicePlusStickerCode ?? "")
                    }
                  >
                    {t("table.local.edit")}
                  </Button>
                </EditOnly>
              </Progress>

            </EntryWrapper>
          </FormWrapper>
        </form>
      </Box>

      <Box component={Paper} sx={{ width: "100%", marginTop: 4 }}>
        <Box sx={{ padding: 2, fontWeight: "bold" }}>
          {t("table.scan.title")}
        </Box>
        <DataTable
          dense
          disableFiltrer={true}
          rows={localScans}
          columns={scansColumns}
          rowsPerPageOptions={[ROWS_PER_PAGE]}
          isLoading={isScansLoading}
        />
      </Box>

      <Box component={Paper} sx={{ width: "100%", marginY: 4 }}>
        <Box sx={{ padding: 2, fontWeight: "bold" }}>
          {t("table.userNotification.title")}
        </Box>
        <DataTable
          dense
          disableFiltrer={true}
          rows={userNotifications}
          columns={userNotificationsColumns}
          rowsPerPageOptions={[ROWS_PER_PAGE]}
          isLoading={isUserNotificationsLoading}
        />
      </Box>
    </>
  );
};

export default LocalEditPage;